import { Injectable } from '@angular/core';
import { DocumentUtilService } from '../document-util.service';
import { SharedServiceRegistry } from '../../shared-service-registry';
import { CoreServiceRegistry } from '../../../core/core-service-registry';
import { DetailedEntityDataService } from '../searchable-entity/detailed-entity/detailed-entity-data.service';
import { ReceiptDataEditorService } from './receipt-data-editor.service';
import { SearchEvent } from '../../search/models/search-event';
import { REFRESH_SEARCH_EVENT } from '../../search/search-constants';
import { SearchService } from '../../search/search.service';
import { SearchParams } from '../../search/models/search-params.model';
import { DataTableConfig } from '../../search/models/data-table-config';
import { SearchStatus } from '../../search/models/search-status.model';


@Injectable()
export class ReceiptDataService extends DetailedEntityDataService {

  public statusTrackingSearchService: SearchService;
  public statusTrackingData = []; // To store metadata of asnststrcktd
  public cellDataMapByService: any = {};
  protected docType: string;
  protected documentUtilService: DocumentUtilService;
  public dataEditor: ReceiptDataEditorService;
  public isExternReceiptKeyEnabled: boolean = false; 
  private uomOrigMap = {
    expectedMasterQty: 'expectedQty',
    receivedMasterQty: 'receivedQty'
  };
  public isUserdefinesEditable: boolean = false;

  constructor(protected coreServiceRegistry: CoreServiceRegistry,
    protected sharedServiceRegistry: SharedServiceRegistry) {
    super(coreServiceRegistry, sharedServiceRegistry);
    this.documentUtilService = sharedServiceRegistry.documentUtilService;
  }

  public initializeReceiptContext(docType) {
    super.initializePrimarySearchContext();
    this.docType = docType;
    this.primarySearchParams.defaultSearchColumn = 'receiptKey';
    this.primarySearchParams.additionalParamMap = { 'docType': docType };
  }

  public getDocType() {
    return this.docType;
  }


  // Method to create new record based on Metadata
  public createNewRecord() {
    const cellDataMap: any = this.documentUtilService.createNewDocumentDataMap(this.primarySearchService.getMetadata(),
      this.primarySearchParams.idProperty, this.getPrimarySelectedDocument(), this.getDocType());
    if (this.primarySearchParams.tableId === 'asntd' && cellDataMap.storerKey) {
      this.dataEditor.setDefaultValues(cellDataMap.storerKey.cVal);
    }
    return cellDataMap;
  }

  // Method  to override default display format for each cell value (in VIEW Mode ONLY)
  public getCellDisplayValue(property: any, isLoading: boolean): any {
    return this.documentUtilService.getCellDisplayValue(property, isLoading, this.primaryDataMap,
      this.primaryMetaDataMap);
  }

  protected getSkuProperty() {
    return this.entityConfig.getType() === 'PO' ? 'commodity' : 'sku';
  }

  public validateLineItems(lineItems) {
    // Fix for JIRA LFWM-250
    if (lineItems.length !== 0) {
      let skuFlag = false;
      let packKeyFlag = false;
      let uomFlag = false;
      // let expectedQtyFlag = false;
      let toLocFlag = false;
      const pattern = /^[0]*(\.\d+)?$/;
      const skuProperty = this.getSkuProperty();
      lineItems.forEach((detail: any, index: any) => {
        detail.cells.forEach(property => {
          if (property.cName === skuProperty && !property.cVal) {
            skuFlag = true;
          } else if (property.cName === 'packKey' && !property.cVal) {
            packKeyFlag = true;
          } else if (property.cName === 'uom' && !property.cVal) {
            uomFlag = true;
          } else if (property.cName === 'expectedQty') {
            /* For JIRA - 1921, the below code is commented to remove mandatory validation for expected qty
             if (!property.cVal || property.cVal === '0') {
              expectedQtyFlag = true;
            } else if (pattern.test(property.cVal.trim())) {
              expectedQtyFlag = true;
            } else {
              // to convert expected qty to 6 decimal places
              this.dataEditor.formatQty('expectedQty', detail);
            }*/
            if (property.cVal && !pattern.test(property.cVal.trim())) {
             // to convert expected qty to 6 decimal places
              this.dataEditor.formatQty('expectedQty', detail);
            }
          } else if (property.cName === 'toLoc' && !property.cVal.trim()) {
            toLocFlag = true;
          } else if (property.cName === 'receivedQty' || property.cName === 'otherUnit1') {
            const propertyName = property.cName;
            this.dataEditor.formatQty(propertyName, detail);
          }
        });
      });
      if (skuFlag) {
        return this.resourceService.get('err_msg_val_commodity');
      } else if (packKeyFlag) {
        return this.resourceService.get('err_msg_val_packkey');
      } else if (uomFlag) {
        return this.resourceService.get('err_msg_val_uom');
      } else if (toLocFlag) {
        return this.resourceService.get('err_msg_val_toloc');
      }
      /* For JIRA - 1921,  the below code is commented to remove mandatory validation for expected qty
      else if (expectedQtyFlag) {
        return this.resourceService.get('err_msg_val_expqty');
      }*/
    }
    return null;
  }

  /**
   * this method used to populate extern receipt key from header based on config key [LFWM-1137]
   */
  public handleAddNewLineItem(event) {
    // setting value for finalized flag as N while adding new row
    if (event.selectedRow.cells['finalizeFlg']) {
      event.selectedRow.cells['finalizeFlg'].cVal = 'N';
    }
    if (this.isExternReceiptKeyEnabled && this.primaryDataMap['externReceiptkey']) {
      event.selectedRow.cells['externReceiptKey'].cVal = this.primaryDataMap['externReceiptkey'].cVal
    }
    super.handleAddNewLineItem(event);
    this.getDetailsSearchService().publishEvent(new SearchEvent(REFRESH_SEARCH_EVENT));
  }

  /**
   * [LFWM-2132] - method to override the primary table [search table] - metadata
   */
  public overrideMetadataMapOfPrimaryTableFields(displayMode: any) {
    if (displayMode === 'E') {
      const detailMetadata = this.getPrimarySearchService().getMetadata()['columnList'];
      for (let i = 0; i < detailMetadata.length; i++) {
        if (detailMetadata[i].propertyName === 'storerKey') {
          detailMetadata[i].displayType = 'V';
          break;
        }
      }
    }
  }

  protected filterKeys(cells: any): string[] {
    // [LFWM-3586] - Removing master uom quantity and uom quantity(if not edited).
    // Backend should update only uom type if uom quantity not updated.
    if(!['asntd', 'potd', 'trtd', 'ixdocktd', 'oxdocktd'].includes(this.primarySearchParams.tableId)){
      return Object.keys(cells);
    }
    return Object.keys(cells).filter(propertyName => {
      if (this.uomOrigMap[propertyName]) {
        return false;
      }
      return this.displayMode === 'C'||
        !Object.values(this.uomOrigMap).includes(propertyName) ||
        cells[propertyName].isEdited;
    });
  }

  // LFWM-3735 this method initializes the value for a new line based on the search service passed
  public getSearchServiceForColumnGroup(columnGroup: string, _tabIndex?: string) {
    if (columnGroup === 'Status Tracking') {
      if (!this.statusTrackingSearchService) {
        if(this.primarySearchParams.tableId === 'asntd'){
        this.statusTrackingSearchService = this.initializeSearchContext('ASNSTSTRCKTD');
        }else if(this.primarySearchParams.tableId === 'trtd'){
          this.statusTrackingSearchService = this.initializeSearchContext('TRSTSTRCKTD');
          }
        // To display key in the storer description dropdown instead of value
        this.statusTrackingSearchService.fetchMetadata().subscribe((metadata: any) => {
          metadata.columnList.forEach((property) => {
            this.statusTrackingData.push(JSON.parse(JSON.stringify(property))); // Unchanged metadata
            if (property.propertyName === 'documentNo') {
              property.values.forEach((option) => {
                option.value = option.key;
              });
            }
          });
          this.statusTrackingSearchService.setMetadata(metadata); // metadata is updated
          // LFWM-1728: Add null entry to facility drop down to remove unexpected error while editing
          this.statusTrackingSearchService.getMetadata().columnList
            .map((data: any) => {
              if (data.elementId === 'facility_Id') {
                data.values.unshift({ key: '', value: '', storerkey: null, userDefineValues: null });
              }
            });
        });
      }
      return this.statusTrackingSearchService;
    }
  }

public initializeSearchContext(tableId: string) {
  const searchParams = new SearchParams();
  searchParams.tableId = tableId;
  searchParams.idProperty = 'rowRef';
  searchParams.tableEditor = this.dataEditor;
  searchParams.isFullFetch = true;
  searchParams.alertService = this.alertService;
  if (this.buttonService && (tableId !== 'ASNSTSTRCKTD' && tableId !== 'TRSTSTRCKTD')) {
      searchParams.actionButtons = this.buttonService.getButtonsForPanel('search-table');
  }
  else {
      searchParams.actionButtons = this.buttonService.getButtonsForPanel('status-tracking-button');
      const tableConfig: DataTableConfig = new DataTableConfig();
      tableConfig.editable = true;
      searchParams.dataTableConfig = tableConfig;
      searchParams.dataTableConfig.isColumnEditable
          = function (params: any) {
              const rowData = params.data;
              const componentRef = params.context.componentParent;
              // if the table is in view mode, do not make any column as editable
              if (['V'].indexOf(componentRef.tableMode) !== -1) {
                  return false;
              }
              const editableCols: Array<string> = ['docStatus'];
              //const isNewRow: boolean = rowData && rowData['isNewItem'] && rowData['isNewItem'].cVal;
              // if this is a new row then all columns will be editable
              // if (isNewRow) {
              //     return (editableCols.indexOf(params.colDef.colId) !== -1);
              // } else {
                  return (['E', 'EL', 'L', 'EA'].indexOf(componentRef.metaDataMap[params.colDef.colId].displayType) > -1);
              //}
          };
  }
  const searchStatus = new SearchStatus();
  return this.sharedServiceRegistry.createSearchService(searchParams, searchStatus);
}

  //LFWM-3637- modified statusTrackLineItems.
  public getModifiedstatusTrackLineItems(searchService: SearchService) {
    const editedRows = searchService.getEditedRows();
    const modifiedLineItems = [];
    if (editedRows.length === 0) {
      return editedRows;
    }
    editedRows.forEach((row: any, index: number) => {
      const modifiedLineItem = [];
      Object.keys(row.cells).forEach((property: any) => {
        const cell = row.cells[property];
        modifiedLineItem.push({ cName: property, cVal: cell.cVal, cValOrig: cell.cValOrig, cValPrev: cell.cValPrev });
      });
      modifiedLineItems.push({ cells: modifiedLineItem });
    });
    return modifiedLineItems;
  }
}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/shared/services/receipt/receipt-data.service.ts